<template>
  <div class="machine-page">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand"><span class="mark">B</span> THE BENEVOLISM SERIES — VOL. V</div>
    </div>

    <main>
      <!-- HERO -->
      <section class="hero">
        <v-container class="hero-inner" style="max-width:1180px;">
          <div class="cover-col intro-rise">
            <div class="cover-card">
              <div class="cover-crown">♛</div>
              <div class="cover-title">THE<br><strong>BENEVOLENT<br>MACHINE</strong></div>
              <div class="cover-sub">How Benevolism Would Transform Technology,<br>Reclaim the Internet, and Build AI That<br>Serves Humanity</div>
              <div class="cover-divider" />
              <div class="cover-scales">⚖</div>
              <div class="cover-divider" />
              <div class="cover-series">The Benevolism Series — Volume V</div>
              <div class="cover-author">ENRICO MICHAEL SEVERE</div>
              <div class="cover-heir">Davidic Heir, Son of King David</div>
            </div>
            <div class="badge">
              <div>
                <div class="badge-top">BENEVOLISM SERIES</div>
                <div class="badge-main">VOLUME</div>
                <div class="badge-bottom">V</div>
              </div>
            </div>
          </div>
          <div class="info-col intro-rise-delayed">
            <div class="eyebrow" style="text-align:left;">Volume V — The Benevolism Series</div>
            <h1 class="info-title">The Benevolent Machine</h1>
            <p class="info-sub">How Benevolism Would Transform Technology, Reclaim the Internet, and Build AI That Serves Humanity</p>
            <p class="info-desc">
              The fifth volume in the Benevolism Series. Five principles of benevolent platform design, ten binding laws, and a five-part Technology Pledge for transforming a technology sector built on the extraction of attention and data into one built to serve the people who use it — because the machines being built right now will shape human civilization for generations.
            </p>
            <div class="stats-row">
              <div v-for="(s, i) in stats" :key="i" class="stat">
                <div class="stat-num">{{ statDisplay[i] }}</div>
                <div class="stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="cta-area">
              <v-btn
                class="btn-gold"
                size="large"
                rounded="0"
                elevation="0"
                :href="amazonReady ? amazonUrl : undefined"
                :target="amazonReady ? '_blank' : undefined"
                :disabled="!amazonReady"
              >
                <v-icon start>mdi-amazon</v-icon>
                {{ amazonReady ? 'Available on Amazon' : 'Coming Soon to Amazon' }}
              </v-btn>
              <div class="coming-note">The Benevolism Series, Volume V — available now.</div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- BLUEPRINT -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Blueprint</div>
          <h2 class="heading">Benevolent Platform Design</h2>
          <p class="lede">
            A comprehensive vision of what a benevolent technology platform looks like when all of its founding principles are operating together — five principles, working as one system.
          </p>
          <div class="layers-grid">
            <div
              v-for="(layer, i) in layers"
              :key="i"
              class="layer-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="layer-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h3 class="layer-name">{{ layer.name }}</h3>
              <p class="layer-desc">{{ layer.desc }}</p>
            </div>
          </div>

          <!-- INTERACTIVE BUSINESS MODEL DEMO -->
          <div class="pricing-demo reveal">
            <h3 class="pricing-heading">Revenue Models in Motion: How a Platform Gets Funded</h3>
            <p class="pricing-sub">Honest revenue works because a platform doesn't need to extract attention and data to be sustainable. Toggle a model.</p>
            <div class="pricing-tabs">
              <button class="pricing-tab" :class="{ active: pricingMode === 'subscription' }" @click="pricingMode = 'subscription'">Honest Subscription</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'sliding' }" @click="pricingMode = 'sliding'">Surveillance Advertising</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'public' }" @click="pricingMode = 'public'">Data Dividend</button>
            </div>

            <transition name="fade-swap" mode="out-in">
              <div v-if="pricingMode === 'subscription'" key="subscription" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill" :style="{ width: subscriptionBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ subscriptionFee }}<span>/mo</span></div>
                </div>
                <p class="pricing-caption">One transparent fee. Every user pays the same amount, every month, for full access — no attention harvested, no data sold.</p>
              </div>

              <div v-else-if="pricingMode === 'sliding'" key="sliding" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill sliding" :style="{ width: slidingBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ slidingCost }}<span>/mo, extracted invisibly</span></div>
                </div>
                <label class="slider-label">
                  Daily Screen Time: <strong>{{ screenHours }} hrs</strong>
                  <input type="range" min="1" max="8" step="0.5" v-model.number="screenHours" class="income-slider" />
                </label>
                <p class="pricing-caption">Drag the slider. The platform is billed "free," but the true cost — in harvested attention and behavioral data — rises with every hour spent.</p>
              </div>

              <div v-else key="public" class="pricing-stage">
                <div class="pricing-visual pool-visual">
                  <div
                    v-for="(dot, n) in poolDots"
                    :key="n"
                    class="pool-dot"
                    :style="{ animationDelay: dot.delay + 's', '--dx': dot.dx + 'px', '--dy': dot.dy + 'px' }"
                  />
                  <div class="pool-center"><div class="pool-icon">◈</div></div>
                </div>
                <p class="pricing-caption">Value generated from personal data flows into a collective trust — governed by and returned to the users who created it, not extracted for shareholders alone.</p>
              </div>
            </transition>
          </div>

          <div class="transition-path">
            <h3 class="transition-heading">The Transition Path</h3>
            <p class="stage-text" style="text-align:center; margin-bottom:32px;">
              Building the benevolent digital economy from the existing extraction-based one is not a single event. It is a managed process of reform that changes business models, algorithms, and governance over time while preserving the genuine connectivity and utility that users depend on.
            </p>
            <div class="timeline-line reveal-line" />
            <div class="transition-stages">
              <div
                v-for="(stage, i) in transitionStages"
                :key="i"
                class="reveal"
                :style="{ transitionDelay: (i * 120) + 'ms' }"
              >
                <div class="stage-label">{{ stage.label }}</div>
                <p class="stage-text">{{ stage.text }}</p>
              </div>
            </div>
            <p class="transition-close">"Different choices produce different machines. A machine designed for user sovereignty rather than behavioral extraction. A machine that illuminates rather than manipulates."</p>
          </div>
        </v-container>
      </section>

      <!-- TEN LAWS -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Doctrine</div>
          <h2 class="heading">The Ten Laws of Benevolent Technology</h2>
          <p class="lede">
            The principles of Benevolism, applied to technology, yield binding standards — the line between technology that serves humanity and technology that extracts from it.
          </p>
          <div class="laws-grid">
            <div
              v-for="(law, i) in laws"
              :key="i"
              class="law-card reveal"
              :style="{ transitionDelay: (i * 40) + 'ms' }"
            >
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
          <div class="pull-quote">
            <div class="mark">"</div>
            <blockquote>The most powerful technologies in history have been used to expand human possibility and to constrain it. Whether they do one or the other is not a property of the technology, but of the choices of those who build it.</blockquote>
          </div>
        </v-container>
      </section>

      <!-- TECHNOLOGY PLEDGE -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Pledge</div>
          <h2 class="heading">The Technology Pledge</h2>
          <p class="lede">
            The specific expression of the Benevolent Pledge, applied to the digital world — offered in five versions: for technologists, for technology organizations, for investors, for users, and for society.
          </p>
          <div class="pledge-tabs">
            <button
              v-for="(pledge, i) in pledges"
              :key="i"
              class="pledge-tab"
              :class="{ active: activePledge === i }"
              @click="activePledge = i"
            >
              {{ pledge.role }}
            </button>
          </div>
          <div class="pledge-panel">
            <transition name="fade-swap" mode="out-in">
              <p :key="activePledge">{{ pledges[activePledge].text }}</p>
            </transition>
          </div>
        </v-container>
      </section>

      <!-- WHAT EACH OF US CAN DO -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">A Call to Action</div>
          <h2 class="heading">What Each of Us Can Do</h2>
          <p class="lede">
            The transformation of technology does not require everyone to become a founder or a regulator. It requires each of us to contribute what we can, from where we stand.
          </p>
          <div class="actions-grid">
            <div
              v-for="(action, i) in actions"
              :key="i"
              class="action-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="action-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="action-title">{{ action.title }}</div>
                <p class="action-text">{{ action.text }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- CLOSING -->
      <section class="closing">
        <v-container class="closing-inner reveal" style="max-width:780px;">
          <div class="mark">"</div>
          <blockquote>The internet was supposed to be the most benevolent machine ever built — a commons of human knowledge, connection, and creativity. It still can be. We only have to choose to build it that way.</blockquote>
          <div class="attr">— Enrico Michael Severe</div>
          <div class="closing-buttons">
            <v-btn class="btn-gold" size="large" rounded="0" elevation="0" @click="scrollToTop">
              Back to the Top
            </v-btn>
            <v-btn class="btn-ghost" size="large" rounded="0" elevation="0" @click="$emit('back')">
              ← Back to Benevolism
            </v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <footer>
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe. Part of
        <a href="#" @click.prevent="$emit('back')">The Benevolism Series</a>. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BenevolentMachine',
  emits: ['back'],
  data: () => ({
    activePledge: 0,
    amazonReady: true,
    amazonUrl: 'https://www.amazon.com/dp/B0H9GRP117/ref=sr_1_6?dib=eyJ2IjoiMSJ9.3vO5FTKeYX3m5Fi9Jlxqkotuc4o3FSqlJhG-J3tQIceS5yk6HOlA5YjbfHg840PXqhOhXbvv3D1GCvsgrKS-dhDZheFrh6d8O_awtQ-ICWmSri2KpRbA_u-OCYLBbNFznKXelt17jZSVHv9DQUu8oUGEDyrxZJCXcb4H_Ds7Wu3Vpj5s1xnlp6fKCU3JUxxQiLb6M1os20x-iVclJm_CqJJ_-MaxDV7OWqkrDXnUQVA.5mZCdkHQUi1XkwGoFhtIyshq92Pmw2Pl-K4zDFQ-lXc&dib_tag=se&qid=1784463372&refinements=p_27%3AEnrico%2BMichael%2BSevere&s=books&sr=1-6&text=Enrico%2BMichael%2BSevere',
    pricingMode: 'subscription',
    subscriptionFee: 12,
    screenHours: 3,
    poolDots: [
      { dx: -90, dy: -40, delay: 0 },
      { dx: 90, dy: -40, delay: 0.5 },
      { dx: -90, dy: 40, delay: 1.0 },
      { dx: 90, dy: 40, delay: 1.5 },
      { dx: 0, dy: -65, delay: 2.0 },
      { dx: 0, dy: 65, delay: 2.5 },
    ],
    stats: [
      { label: 'Principles', value: 5 },
      { label: 'Laws', value: 10 },
      { label: 'Pledges', value: 5 },
      { label: 'Series Volume', value: 'V' },
    ],
    statDisplay: [0, 0, 0, 'V'],
    layers: [
      {
        name: 'User Sovereignty',
        desc: 'The platform exists to serve the user\'s genuine interests, not to extract their attention, data, or money. Every design decision is evaluated against what serves the user — not what maximizes engagement, data collection, or advertising revenue.',
      },
      {
        name: 'Transparent Algorithms',
        desc: 'Users have the right to understand, at a level of meaningful detail, how the algorithms that determine what they see and experience are operating. Opacity that prevents this understanding is incompatible with sovereignty.',
      },
      {
        name: 'Honest Revenue',
        desc: 'Platforms are funded through honest pricing for genuine services rather than the surveillance-advertising model that monetizes attention and data. If a platform can\'t sustain itself at a price users will pay, it isn\'t delivering genuine value.',
      },
      {
        name: 'Exit Rights',
        desc: 'Users have the genuine right to leave any platform, taking their data and connections with them, without penalty. Lock-in strategies that make exit costly are incompatible with user sovereignty and competitive markets.',
      },
      {
        name: 'Wellbeing Measurement',
        desc: 'Platform success is measured by genuine user wellbeing — whether the platform adds value to users\' lives — not by time on platform, daily active users, and advertising impressions.',
      },
    ],
    transitionStages: [
      {
        label: 'Begins With',
        text: 'The choice of technologists and founders to design for user wellbeing over engagement extraction, and to fund their businesses through honest revenue rather than surveillance advertising.',
      },
      {
        label: 'Proceeds Through',
        text: 'Platforms that adopt data sovereignty, algorithmic transparency, and interoperability; investors who back genuine value creation over extraction machines; and regulators who enforce structural remedies for platform monopoly.',
      },
      {
        label: 'Culminates In',
        text: 'A digital economy where technology serves human flourishing — where AI is genuinely aligned with humanity\'s interests, and the internet is restored as the commons it was always meant to be.',
      },
    ],
    laws: [
      { numeral: 'I', name: 'The Law of User Sovereignty', desc: 'Technology products exist to serve the genuine interests of the users who use them. Technology that exploits cognitive vulnerabilities, manufactures dependency, or extracts value from users without genuine authorization is not serving users. It is preying on them.' },
      { numeral: 'II', name: 'The Law of Honest Revenue', desc: 'Technology platforms must be funded by honest exchange of value at prices that reflect genuine cost plus a fair return. Surveillance advertising is not honest revenue. It is concealed extraction.' },
      { numeral: 'III', name: 'The Law of Data Sovereignty', desc: 'Personal data is a dimension of the self, not a natural resource. It belongs to the person it describes. Organizations that collect it do so as stewards, not owners.' },
      { numeral: 'IV', name: 'The Law of Algorithmic Transparency', desc: 'Algorithms that make or influence consequential decisions about human beings must be explainable to those affected, auditable by independent parties, and contestable by those harmed.' },
      { numeral: 'V', name: 'The Law of Anti-Manipulation', desc: 'No feature, algorithm, or design pattern may exploit the cognitive vulnerabilities of users for commercial advantage. Technology must be designed for user wellbeing, not for the extraction of attention.' },
      { numeral: 'VI', name: 'The Law of Interoperability', desc: 'Dominant platforms must interoperate with competing services and enable users to transfer their data and connections to alternative platforms without penalty. The right to exit is the foundation of genuine choice.' },
      { numeral: 'VII', name: 'The Law of AI Alignment', desc: 'AI systems must be developed and deployed in genuine alignment with the interests of the full range of humanity they affect — subject to safety evaluation, harm auditing, and governance that ensures accountability.' },
      { numeral: 'VIII', name: 'The Law of Creator Rights', desc: 'AI systems trained on human creative work must respect the rights of the creators who make them possible — transparent provenance, meaningful opt-out, and fair compensation for commercial use.' },
      { numeral: 'IX', name: 'The Law of Digital Inclusion', desc: 'Meaningful participation in the digital economy is a right of citizenship, not a privilege of income. Every person deserves broadband access, current devices, and digital literacy.' },
      { numeral: 'X', name: 'The Law of Technological Accountability', desc: 'Those who build and deploy technology are responsible for its consequences — including foreseeable harms — not merely its intended ones. Correcting harmful systems is an obligation, not an option.' },
    ],
    pledges: [
      { role: 'Technologist', text: 'I chose this work because technology has the power to solve real problems and expand human possibility. I commit to practicing it in alignment with that belief. I will design for user wellbeing, not for engagement metrics that serve extraction. I will advocate within my organization for the product and business model decisions that align our work with genuine value creation. I will refuse to build features I know to be manipulative, systems I know to be biased, or architectures I know to be harmful. I will use my technical skill as a force for the world I want to inhabit, not merely for the incentive structures I am given.' },
      { role: 'Organization', text: 'This organization was built to solve genuine problems and create genuine value. We commit to funding ourselves through honest exchange rather than through the extraction of attention and behavioral data. We will design our products for user wellbeing and measure our success by whether users\' lives are genuinely improved by what we build. We will protect user data as a right, not exploit it as an asset. We will make our algorithms transparent and our business model honest. We will govern ourselves in ways that give users and affected communities a genuine voice in the decisions we make about the systems that shape their digital lives.' },
      { role: 'Investor', text: 'I commit to deploying capital in technology in alignment with the principles of Benevolism. I will back founders who are building genuine value through honest business models, not those who are building extraction machines at scale. I will set return expectations that are consistent with the time horizon required to build benevolent technology businesses. I will refuse to pressure the organizations I back to adopt surveillance advertising, dark patterns, or other extractive practices in pursuit of short-term metrics. I will measure the success of my technology portfolio by the genuine value it creates for users and society, not merely by the financial returns it generates.' },
      { role: 'User', text: 'I commit to participating in the digital economy with awareness of the choices I am making and their consequences. I will support, where I can, the technology organizations that operate on benevolent terms — that charge honestly for genuine value rather than funding themselves through my attention and data. I will advocate for the digital rights I deserve: privacy, transparency, data sovereignty, and freedom from algorithmic manipulation. I will hold the technology platforms I use accountable for the harms their design choices produce. And I will support the policy frameworks that make benevolent technology the standard rather than the exception.' },
      { role: 'Society', text: 'We commit to governing technology in alignment with human values. We will regulate the extraction mechanisms that have captured the digital commons and redirect the incentives of the technology industry toward genuine value creation. We will invest in digital public infrastructure that ensures universal access to the opportunities of the digital economy. We will protect the privacy, autonomy, and dignity of people in their digital lives with the same seriousness that we protect them in physical space. We will govern the development of artificial intelligence with the care that its civilizational stakes require. And we will measure the success of the digital economy not by the market capitalization of its dominant platforms but by the quality of the digital lives of the people who inhabit it.' },
    ],
    actions: [
      { title: 'Technologists', text: 'Choose the organizations whose values align with benevolent design, advocate within your organization for user-serving product and business model choices, and refuse to apply your capabilities to systems whose harmfulness you understand.' },
      { title: 'Founders', text: 'Make the architectural choices of your business model with long-term consequences in view — honest revenue over surveillance advertising, wellbeing over engagement, governance that aligns your incentives with your users\'.' },
      { title: 'Investors', text: 'Back the founders building genuine value rather than extraction machines, set the time horizons benevolent technology businesses require, and resist pressure toward dark-pattern monetization.' },
      { title: 'Users & Policymakers', text: 'Choose the services that operate on honest terms, hold the platforms you use accountable for the harms their design produces, and support the policy frameworks that protect digital rights and restore competition.' },
    ],
  }),
  computed: {
    subscriptionBarPct () {
      return Math.round((this.subscriptionFee / 30) * 100)
    },
    slidingCost () {
      const raw = this.screenHours * 32
      return Math.max(15, Math.min(400, Math.round(raw / 5) * 5))
    },
    slidingBarPct () {
      return Math.round((this.slidingCost / 400) * 100)
    },
  },
  mounted () {
    window.scrollTo({ top: 0 })
    this.animateStats()
    this.$nextTick(() => this.setupReveal())
  },
  beforeUnmount () {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    animateStats () {
      const duration = 900
      this.stats.forEach((s, i) => {
        if (typeof s.value !== 'number') {
          this.$set(this.statDisplay, i, s.value)
          return
        }
        const target = s.value
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          this.$set(this.statDisplay, i, Math.round(target * progress))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    setupReveal () {
      const els = this.$el.querySelectorAll('.reveal, .reveal-line')
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
      els.forEach((el) => observer.observe(el))
      this._revealObserver = observer
    },
  },
}
</script>

<style>
.machine-page {
  --steel: #0B242C;
  --steel-mid: #123642;
  --steel-light: #1B4B59;
  background: var(--steel);
  color: var(--ivory);
  font-family: var(--font-body);
}

/* ===== ENTRANCE + SCROLL REVEAL ===== */
@keyframes introRise {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
.machine-page .intro-rise { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) both; }
.machine-page .intro-rise-delayed { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) 0.12s both; }

.machine-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.machine-page .reveal.is-visible { opacity: 1; transform: translateY(0); }

.machine-page .reveal-line {
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s ease;
}
.machine-page .reveal-line.is-visible { transform: scaleX(1); }

.machine-page .top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(11,36,44,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
}
.machine-page .back-link {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--gold-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.machine-page .back-link:hover { color: var(--gold); }
.machine-page .top-brand {
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--ivory-dim);
  display: flex;
  align-items: center;
  gap: 8px;
}
.machine-page .top-brand .mark {
  width: 24px; height: 24px;
  background: var(--gold);
  color: var(--steel);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
}

.machine-page .eyebrow {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--gold);
  text-transform: uppercase;
  margin-bottom: 16px;
}
.machine-page .heading {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: var(--ivory);
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.machine-page .lede {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.75;
  color: var(--ivory-dim);
  font-weight: 300;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.machine-page section { padding: 96px 0; }
.machine-page section.alt { background: var(--steel-mid); border-top: 1px solid rgba(201,168,76,0.1); border-bottom: 1px solid rgba(201,168,76,0.1); }

.machine-page .hero { padding: 72px 0 96px; position: relative; overflow: hidden; }
.machine-page .hero::before {
  content: '';
  position: absolute;
  top: -200px; right: -200px;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.machine-page .hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 1;
}
@media (max-width: 900px) {
  .machine-page .hero-inner { grid-template-columns: 1fr; }
  .machine-page .cover-col { display: flex; flex-direction: column; align-items: center; }
}
.machine-page .cover-col { position: relative; }
.machine-page .cover-card {
  width: 260px;
  min-height: 360px;
  background: linear-gradient(160deg, #1B4B59 0%, #0B242C 60%, #030D11 100%);
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 0 0 0 1px rgba(201,168,76,0.1), 8px 8px 32px rgba(0,0,0,0.6), inset 0 0 60px rgba(201,168,76,0.03);
  padding: 32px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
}
.machine-page .cover-card::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(201,168,76,0.2);
  pointer-events: none;
}
.machine-page .cover-card::after {
  content: '';
  position: absolute;
  top: 0; left: -150%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(232,204,128,0.18), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s ease;
  pointer-events: none;
}
.machine-page .cover-card:hover::after { left: 150%; }
.machine-page .cover-card:hover { transform: rotate(0deg) scale(1.02); }
.machine-page .cover-crown { font-size: 34px; margin-bottom: 2px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.machine-page .cover-title {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 400;
  color: var(--gold-light);
  letter-spacing: 0.08em;
  line-height: 1.4;
}
.machine-page .cover-title strong { font-size: 21px; font-weight: 900; display: block; letter-spacing: 0.02em; }
.machine-page .cover-divider {
  width: 80%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  margin: 4px 0;
}
.machine-page .cover-sub { font-family: var(--font-body); font-size: 11px; color: var(--ivory-dim); line-height: 1.5; font-style: italic; }
.machine-page .cover-scales { font-size: 30px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); margin: 4px 0; }
.machine-page .cover-series { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.15em; color: var(--ivory-dim); }
.machine-page .cover-author { font-family: var(--font-display); font-size: 12px; font-weight: 700; color: var(--gold-light); letter-spacing: 0.12em; }
.machine-page .cover-heir { font-family: var(--font-body); font-size: 9px; font-style: italic; color: var(--ivory-dim); }

.machine-page .badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 76px; height: 76px;
  background: var(--gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--steel);
  animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) 0.5s both;
}
@keyframes badgePop {
  from { opacity: 0; transform: rotate(15deg) scale(0.4); }
  to { opacity: 1; transform: rotate(15deg) scale(1); }
}
.machine-page .badge-top { font-family: var(--font-ui); font-size: 7px; font-weight: 700; letter-spacing: 0.08em; }
.machine-page .badge-main { font-family: var(--font-display); font-size: 11px; font-weight: 900; line-height: 1.1; }
.machine-page .badge-bottom { font-family: var(--font-ui); font-size: 8px; font-weight: 600; }

.machine-page .info-title {
  font-family: var(--font-display);
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 700;
  color: var(--ivory);
  margin-bottom: 8px;
  line-height: 1.15;
}
.machine-page .info-sub {
  font-family: var(--font-display);
  font-size: 17px;
  font-style: italic;
  color: var(--gold-dim);
  margin-bottom: 22px;
  line-height: 1.5;
}
.machine-page .info-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.75;
  color: var(--ivory-dim);
  margin-bottom: 32px;
  font-weight: 300;
  max-width: 620px;
}
.machine-page .stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 36px;
  border: 1px solid rgba(201,168,76,0.15);
  width: fit-content;
}
.machine-page .stat { padding: 16px 26px; text-align: center; position: relative; }
.machine-page .stat:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60px;
  background: rgba(201,168,76,0.15);
}
.machine-page .stat-num { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); line-height: 1; }
.machine-page .stat-label { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.13em; color: var(--slate-light); text-transform: uppercase; margin-top: 4px; }

.machine-page .cta-area { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.machine-page .btn-gold { background: var(--gold) !important; color: var(--steel) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 700 !important; letter-spacing: 0.08em !important; padding: 0 32px !important; height: 52px !important; }
.machine-page .btn-gold:hover { background: var(--gold-light) !important; }
.machine-page .coming-note { font-family: var(--font-ui); font-size: 11px; color: var(--slate-light); letter-spacing: 0.02em; }

.machine-page .layers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2px; margin-bottom: 56px; }
.machine-page .layer-card { background: var(--steel); border: 1px solid rgba(201,168,76,0.08); padding: 32px 28px; transition: border-color 0.2s, background 0.2s; }
.machine-page .layer-card:hover { border-color: rgba(201,168,76,0.25); background: var(--steel-light); }
.machine-page .layer-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; margin-bottom: 12px; }
.machine-page .layer-name { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.machine-page .layer-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.machine-page .transition-path { max-width: 920px; margin: 0 auto; text-align: center; }
.machine-page .transition-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 32px; }
.machine-page .timeline-line {
  height: 2px;
  max-width: 760px;
  margin: 0 auto 32px;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold), var(--gold-dim));
}
.machine-page .transition-stages { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; text-align: left; margin-bottom: 32px; }
@media (max-width: 768px) { .machine-page .transition-stages { grid-template-columns: 1fr; } }
.machine-page .stage-label { font-family: var(--font-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; color: var(--gold); text-transform: uppercase; margin-bottom: 10px; }
.machine-page .stage-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }
.machine-page .transition-close { font-family: var(--font-display); font-style: italic; font-size: 15px; color: var(--ivory-dim); text-align: center; }

/* ===== INTERACTIVE PRICING DEMO ===== */
.machine-page .pricing-demo { max-width: 720px; margin: 0 auto 64px; text-align: center; }
.machine-page .pricing-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 8px; }
.machine-page .pricing-sub { font-family: var(--font-body); font-size: 14px; color: var(--ivory-dim); font-weight: 300; margin-bottom: 28px; }
.machine-page .pricing-tabs { display: flex; gap: 8px; justify-content: center; margin-bottom: 32px; flex-wrap: wrap; }
.machine-page .pricing-tab {
  background: transparent;
  border: 1px solid rgba(201,168,76,0.2);
  color: var(--ivory-dim);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.machine-page .pricing-tab:hover { border-color: var(--gold); color: var(--gold); }
.machine-page .pricing-tab.active { background: var(--gold); border-color: var(--gold); color: var(--steel); }

.machine-page .pricing-stage { min-height: 200px; }
.machine-page .pricing-visual { display: flex; flex-direction: column; align-items: center; gap: 16px; margin-bottom: 20px; }
.machine-page .price-bar-track {
  width: 100%;
  max-width: 360px;
  height: 14px;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.2);
  overflow: hidden;
}
.machine-page .price-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold));
  transition: width 0.35s ease;
}
.machine-page .price-readout { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); }
.machine-page .price-readout span { font-family: var(--font-ui); font-size: 13px; font-weight: 500; color: var(--ivory-dim); margin-left: 4px; }

.machine-page .slider-label {
  display: block;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--ivory-dim);
  letter-spacing: 0.04em;
  margin-bottom: 16px;
}
.machine-page .slider-label strong { color: var(--gold); font-weight: 700; }
.machine-page .income-slider {
  display: block;
  width: 100%;
  max-width: 360px;
  margin: 10px auto 0;
  appearance: none;
  -webkit-appearance: none;
  height: 4px;
  background: rgba(201,168,76,0.2);
  border-radius: 2px;
  outline: none;
}
.machine-page .income-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--steel);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.machine-page .income-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
.machine-page .income-slider::-moz-range-thumb {
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--steel);
  cursor: pointer;
}
.machine-page .pricing-caption {
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ivory-dim);
  font-weight: 300;
  max-width: 460px;
  margin: 0 auto;
}

.machine-page .pool-visual { position: relative; width: 200px; height: 140px; margin: 0 auto; }
.machine-page .pool-center {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  width: 56px; height: 56px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--gold) 0%, var(--gold-dim) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 24px rgba(201,168,76,0.4);
}
.machine-page .pool-icon { font-size: 22px; color: var(--steel); }
.machine-page .pool-dot {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--gold-light);
  top: 50%; left: 50%;
  margin: -4px;
  opacity: 0;
  animation: flowIn 3s ease-in infinite;
}
@keyframes flowIn {
  0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); }
  15% { opacity: 1; }
  85% { opacity: 1; transform: translate(calc(var(--dx) * 0.08), calc(var(--dy) * 0.08)) scale(0.5); }
  100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
}

/* fade-swap transition, used for pledge text and pricing stage swaps */
/* Vue 3 naming */
.machine-page .fade-swap-enter-active,
.machine-page .fade-swap-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.machine-page .fade-swap-enter-from { opacity: 0; transform: translateY(6px); }
.machine-page .fade-swap-leave-to { opacity: 0; transform: translateY(-6px); }
/* Vue 2 naming (same active/to classes, "from" state is unsuffixed) */
.machine-page .fade-swap-enter { opacity: 0; transform: translateY(6px); }
.machine-page .fade-swap-leave { opacity: 0; transform: translateY(-6px); }

.machine-page .laws-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-bottom: 48px; }
@media (max-width: 768px) { .machine-page .laws-grid { grid-template-columns: 1fr; } }
.machine-page .law-card { display: flex; gap: 22px; padding: 32px 36px; background: var(--steel); border: 1px solid rgba(201,168,76,0.06); transition: border-color 0.2s, background 0.2s; }
.machine-page .law-card:hover { border-color: rgba(201,168,76,0.2); background: var(--steel-light); }
.machine-page .law-numeral { font-family: var(--font-display); font-size: 13px; font-style: italic; color: var(--gold-dim); flex-shrink: 0; padding-top: 2px; width: 22px; }
.machine-page .law-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--ivory); margin-bottom: 9px; }
.machine-page .law-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.machine-page .pull-quote { max-width: 640px; margin: 0 auto; text-align: center; }
.machine-page .pull-quote .mark { font-family: var(--font-display); font-size: 48px; color: var(--gold); line-height: 1; }
.machine-page .pull-quote blockquote { font-family: var(--font-display); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ivory); margin: 4px 0 0; }

.machine-page .pledge-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 40px; }
.machine-page .pledge-tab { background: transparent; border: 1px solid rgba(201,168,76,0.2); color: var(--ivory-dim); font-family: var(--font-ui); font-size: 12px; font-weight: 600; letter-spacing: 0.05em; padding: 11px 22px; cursor: pointer; transition: all 0.2s; }
.machine-page .pledge-tab:hover { border-color: var(--gold); color: var(--gold); }
.machine-page .pledge-tab.active { background: var(--gold); border-color: var(--gold); color: var(--steel); }
.machine-page .pledge-panel { max-width: 760px; margin: 0 auto; background: var(--steel-mid); border: 1px solid rgba(201,168,76,0.12); padding: 44px 48px; }
.machine-page .pledge-panel p { font-family: var(--font-body); font-size: 15px; line-height: 1.8; color: var(--ivory-dim); font-weight: 300; }

.machine-page .actions-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2px; }
.machine-page .action-card { display: flex; gap: 20px; padding: 34px; background: var(--steel-mid); border: 1px solid rgba(201,168,76,0.08); transition: border-color 0.2s, background 0.2s; }
.machine-page .action-card:hover { border-color: rgba(201,168,76,0.25); background: var(--steel-light); }
.machine-page .action-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; padding-top: 4px; width: 26px; flex-shrink: 0; }
.machine-page .action-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.machine-page .action-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.machine-page .closing { text-align: center; padding: 110px 0 96px; position: relative; overflow: hidden; }
.machine-page .closing::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.machine-page .closing-inner { position: relative; z-index: 1; }
.machine-page .closing .mark { font-family: var(--font-display); font-size: 56px; color: var(--gold); line-height: 1; }
.machine-page .closing blockquote { font-family: var(--font-display); font-style: italic; font-size: clamp(22px, 3vw, 30px); line-height: 1.5; color: var(--ivory); margin: 8px 0 20px; }
.machine-page .closing .attr { font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.15em; color: var(--gold-dim); text-transform: uppercase; margin-bottom: 48px; }
.machine-page .closing-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
.machine-page .btn-ghost { background: transparent !important; border: 1px solid rgba(245,240,232,0.25) !important; color: var(--ivory-dim) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 500 !important; letter-spacing: 0.06em !important; padding: 0 32px !important; height: 52px !important; }
.machine-page .btn-ghost:hover { border-color: var(--ivory-dim) !important; color: var(--ivory) !important; }

.machine-page footer { padding: 40px 0; background: var(--steel-mid); border-top: 1px solid rgba(201,168,76,0.12); text-align: center; }
.machine-page .footer-text { font-family: var(--font-ui); font-size: 11px; color: rgba(200,192,176,0.5); letter-spacing: 0.05em; }
.machine-page .footer-text a { color: var(--gold-dim); text-decoration: none; cursor: pointer; }
.machine-page .footer-text a:hover { color: var(--gold); }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .machine-page * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .machine-page .reveal,
  .machine-page .reveal-line {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>